import { formatDistanceToNow } from 'date-fns';
import { parseLocalDate } from '@/utils';
import {
  PaintBrushIcon,
  CodeBracketIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import { StatusIndicator } from '@/components/features/projects/StatusIndicator';
import { useProjectRisk } from '@/hooks/useProjectRisk';
import type { RiskStatus } from '@/utils/completionDateCalculation';
import { STATUS_BORDER_COLORS } from '@/utils/statusColors';
import type { Database } from '../../../types/database.types';

type Project = Database['public']['Tables']['projects']['Row'];

interface ProjectCardProps {
  project: Project;
  onClick: (projectId: number) => void;
  taskCounts?: {
    design: number;
    development: number;
    qa: number;
    total: number;
  };
  progressPercentage?: number;
}

const CARD_STYLE: Record<RiskStatus, { bg: string; borderColor: string; progress: string }> = {
  on_track: { bg: 'bg-success-50/40',  borderColor: STATUS_BORDER_COLORS.on_track, progress: 'bg-success-500' },
  at_risk:  { bg: 'bg-warning-50/40',  borderColor: STATUS_BORDER_COLORS.at_risk,  progress: 'bg-warning-500' },
  overdue:  { bg: 'bg-danger-50/40',   borderColor: STATUS_BORDER_COLORS.overdue,  progress: 'bg-danger-500'  },
  unknown:  { bg: 'bg-gray-50/40',     borderColor: STATUS_BORDER_COLORS.unknown,  progress: 'bg-gray-400'    },
};

export const ProjectCard = ({
  project,
  onClick,
  taskCounts = { design: 0, development: 0, qa: 0, total: 0 },
  progressPercentage = 0,
}: ProjectCardProps) => {
  const riskInfo = useProjectRisk(project.id);
  const displayStatus: RiskStatus = riskInfo?.riskStatus ?? 'unknown';
  const cardStyle = CARD_STYLE[displayStatus];

  const committedDate = parseLocalDate(project.committed_delivery_date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const estimatedDate = riskInfo?.estimatedCompletion
    ? riskInfo.estimatedCompletion.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : project.estimated_completion_date
    ? parseLocalDate(project.estimated_completion_date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Not set';

  let lastUpdated: string;
  try {
    lastUpdated = formatDistanceToNow(new Date(project.updated_at), { addSuffix: true });
  } catch {
    lastUpdated = 'unknown';
  }

  const deltaDays =
    riskInfo?.isAtRisk && riskInfo.daysDifference > 0 ? riskInfo.daysDifference : null;

  return (
    <div
      onClick={() => onClick(project.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(project.id);
        }
      }}
      role="button"
      tabIndex={0}
      className={`relative rounded-lg shadow border border-gray-200 p-6 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 border-l-4 ${cardStyle.bg}`}
      style={{ borderLeftColor: cardStyle.borderColor }}
      aria-label={`View project: ${project.name}. Status: ${displayStatus.replace('_', ' ')}`}
    >
      {/* Risk Indicator Badge - Top Left Corner per wireframe Section 1.3 */}
      <div className="absolute top-4 left-6">
        <StatusIndicator status={displayStatus} />
      </div>

      {/* Project Name - Clickable, Semibold */}
      <h3 className="text-lg font-semibold text-gray-900 mb-3 pl-32">
        {project.name}
      </h3>

      {/* Dates */}
      <div className="space-y-1 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Committed Date:</span>
          <span className="font-medium text-gray-900 font-mono">{committedDate}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Estimated Completion:</span>
          <span className="font-medium text-gray-900 font-mono">{estimatedDate}</span>
        </div>
        {deltaDays !== null && (
          <div className="text-sm font-medium text-red-600">
            {deltaDays} working {deltaDays === 1 ? 'day' : 'days'} late
          </div>
        )}
      </div>

      {/* Progress Bar with Percentage */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-gray-600">Progress</span>
          <span className="font-medium text-gray-900">{progressPercentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`${cardStyle.progress} h-2 rounded-full transition-all duration-300`}
            style={{ width: `${progressPercentage}%` }}
            role="progressbar"
            aria-valuenow={progressPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Project progress: ${progressPercentage}%`}
          />
        </div>
      </div>

      {/* Task Breakdown by Type with Icons */}
      <div className="flex items-center gap-4 mb-3">
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <PaintBrushIcon className="w-4 h-4 text-purple-500" aria-hidden="true" />
          <span className="font-medium">{taskCounts.design}</span>
          <span className="sr-only">design tasks</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <CodeBracketIcon className="w-4 h-4 text-blue-600" aria-hidden="true" />
          <span className="font-medium">{taskCounts.development}</span>
          <span className="sr-only">development tasks</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <CheckBadgeIcon className="w-4 h-4 text-emerald-600" aria-hidden="true" />
          <span className="font-medium">{taskCounts.qa}</span>
          <span className="sr-only">QA tasks</span>
        </div>
        <div className="ml-auto text-sm text-gray-600">
          <span className="font-medium">{taskCounts.total}</span> tasks total
        </div>
      </div>

      {/* Last Updated Timestamp */}
      <div className="text-xs text-gray-600">
        Last updated {lastUpdated}
      </div>
    </div>
  );
};
